import * as React from 'react';
import { FocusEvent, ReactNode } from 'react';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason
} from '@mui/base/useAutocomplete/useAutocomplete';
import { ChipTypeMap, AutocompleteValue } from '@mui/material';
import { FieldProps } from 'formik';
import { Autocomplete, AutocompleteProps } from '../Autocomplete/Autocomplete';
import { transformOptions, transformOptionValue } from './util/tranformOptionsData';

export function FormikAutocomplete<
  A,
  B extends boolean | undefined,
  C extends boolean | undefined,
  D extends boolean | undefined,
  E extends React.ElementType = ChipTypeMap['defaultComponent']
>({
  field,
  form,
  meta,
  onChange,
  onBlur,
  ...props
}: AutocompleteProps<A, B, C, D, E> & FieldProps) {
  const onBlurEvent = (e: FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);

    if (onBlur) {
      onBlur(e);
    }
  };

  const onChangeEvent = (
    e: React.SyntheticEvent,
    v: AutocompleteValue<A, B, C, D>,
    r: AutocompleteChangeReason,
    d?: AutocompleteChangeDetails<A>
  ) => {
    const transformedValue = transformOptions(v, !!props.multiple);

    form.setFieldValue(field.name, transformedValue);

    if (onChange) {
      onChange(e, v, r, d);
    }
  };

  const errors = form.errors[field.name] && form.touched[field.name];

  const fieldValue = props.multiple
    ? transformOptions(field.value, true) ||
      [].map(
        value =>
          props.options.find(
            option =>
              transformOptionValue(option as { label: string; value: string } | string) === value
          ) || value
      )
    : props.options.find(option =>
        option !== null && typeof option === 'object'
          ? 'value' in option && option.value === field.value
          : option === field.value
      );

  const AutocompleteWithTsFixes = Autocomplete as (
    props: AutocompleteProps<A, B, C, D, E>
  ) => ReactNode;

  return (
    <AutocompleteWithTsFixes
      {...props}
      textFieldProps={{ ...props.textFieldProps, error: !!errors }}
      value={fieldValue as AutocompleteValue<A, B, C, D>}
      onChange={onChangeEvent}
      onBlur={onBlurEvent}
    />
  );
}
